import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { FileText, Download, CheckCircle, Shield, Eye, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CertificateProposal() {
  const { t } = useLanguage();
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (type) => {
    setDownloading(type);
    try {
      const endpoint = type === 'enhanced' 
        ? `${BACKEND_URL}/api/samples/certificate-enhanced`
        : `${BACKEND_URL}/api/samples/proposal`;
      
      const response = await fetch(endpoint);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = type === 'enhanced' ? 'sample_certificate_enhanced.pdf' : 'proposal_comparison.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00A651] rounded-full mb-4">
            <Award className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('certificateProposal.title')}</h1>
          <p className="text-gray-600">{t('certificateProposal.subtitle')}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-gray-400"></div>
              <h3 className="font-semibold text-gray-900">{t('certificateProposal.currentCert')}</h3>
            </div>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2"><span className="text-gray-400">-</span>{t('certificateProposal.simpleDesign')}</li>
              <li className="flex items-start gap-2"><span className="text-gray-400">-</span>{t('certificateProposal.noLogo')}</li>
              <li className="flex items-start gap-2"><span className="text-gray-400">-</span>{t('certificateProposal.noVerification')}</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-[#e6f6ec] to-white rounded-xl shadow-sm border-2 border-[#00A651] p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-[#00A651]"></div>
              <h3 className="font-semibold text-gray-900">{t('certificateProposal.improvedCert')}</h3>
              <span className="text-xs bg-[#00A651] text-white px-2 py-0.5 rounded-full">NEW</span>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#00A651] mt-0.5 flex-shrink-0" />{t('certificateProposal.addLogo')}</li>
              <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#00A651] mt-0.5 flex-shrink-0" />{t('certificateProposal.qrCode')}</li>
              <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#00A651] mt-0.5 flex-shrink-0" />{t('certificateProposal.premiumDesign')}</li>
              <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#00A651] mt-0.5 flex-shrink-0" />{t('certificateProposal.officialSeal')}</li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="font-semibold text-gray-900 mb-4">{t('certificateProposal.expectedBenefits')}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">{t('certificateProposal.security')}</h4>
                <p className="text-sm text-gray-600">{t('certificateProposal.securityDesc')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                <Eye className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">{t('certificateProposal.brand')}</h4>
                <p className="text-sm text-gray-600">{t('certificateProposal.brandDesc')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Award className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">{t('certificateProposal.trust')}</h4>
                <p className="text-sm text-gray-600">{t('certificateProposal.trustDesc')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                <FileText className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">{t('certificateProposal.convenience')}</h4>
                <p className="text-sm text-gray-600">{t('certificateProposal.convenienceDesc')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 rounded-xl p-6 text-center">
          <h3 className="text-white font-semibold mb-2">{t('certificateProposal.downloadSample')}</h3>
          <p className="text-gray-400 text-sm mb-6">{t('certificateProposal.downloadDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => handleDownload('enhanced')} disabled={downloading === 'enhanced'} className="bg-[#00A651] hover:bg-[#008a43] text-white">
              <Download className="h-4 w-4 mr-2" />
              {downloading === 'enhanced' ? t('certificateProposal.downloading') : t('certificateProposal.improvedSample')}
            </Button>
            <Button onClick={() => handleDownload('proposal')} disabled={downloading === 'proposal'} variant="outline" className="border-white text-white hover:bg-white/10">
              <FileText className="h-4 w-4 mr-2" />
              {downloading === 'proposal' ? t('certificateProposal.downloading') : t('certificateProposal.proposalPdf')}
            </Button>
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-gray-600 mb-4">{t('certificateProposal.implementQuestion')}</p>
          <Link to="/">
            <Button variant="outline" className="mr-2">
              {t('certificateProposal.backToHome')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
